'use client';

import { useState, useRef } from 'react';
import { Star, ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';

const TESTIMONIALS = [
  {
    id: 1,
    name: 'Sarah Jones',
    role: 'Schengen Visa Applicant',
    review:
      'The flight itinerary document was generated within 35 minutes! Submitted it at VFS for my Schengen visa and received approval with zero issues. Highly recommend it!',
    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=100&h=100',
    rating: 5,
    verified: 'Verified Booking',
  },
  {
    id: 2,
    name: 'Michael Brown',
    role: 'UK Standard Visitor Visa',
    review:
      'A game-changer! UKVI explicitly advises not buying non-refundable tickets beforehand. This itinerary saved me over $1,200 in pre-booking risks. Fantastic quality!',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100&h=100',
    rating: 5,
    verified: 'Verified Booking',
  },
  {
    id: 3,
    name: 'Emily White',
    role: 'Onward Travel Proof',
    review:
      'Fantastic quality and customer service. Was boarding a flight to Asia and needed proof of onward travel. Received the PDF on my phone in minutes. We couldn’t be happier!',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=100&h=100',
    rating: 5,
    verified: 'Verified Booking',
  },
  {
    id: 4,
    name: 'Marcus Thorne',
    role: 'Canada Tourist Stream',
    review:
      'Super fast turnaround! I had a small name spelling correction; emailed support and they sent an updated flight reservation PDF within 10 minutes free of charge.',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=100&h=100',
    rating: 5,
    verified: 'Verified Booking',
  },
  {
    id: 5,
    name: 'Sophia Martinez',
    role: 'Japan Tourist Visa',
    review:
      'Clear GDS PNR layout with exact airline flight codes and schedules. The document format is clean, professional, and looks 100% authentic for consular filing.',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=100&h=100',
    rating: 5,
    verified: 'Verified Booking',
  },
];

export default function Testimonials() {
  const scrollRef = useRef(null);

  const scrollTo = (direction) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const scrollAmount = container.clientWidth * 0.75;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <section
      id="testimonials"
      className="relative w-full py-16 sm:py-24 overflow-hidden bg-gradient-to-b from-[#eaf6fa] via-[#e2f2f8] to-[#cbecf7]"
      aria-labelledby="testimonials-heading"
    >
      {/* ── Soft Travel World Map & Flight Loop Background ────── */}
      <div className="pointer-events-none absolute inset-0 -z-0 overflow-hidden" aria-hidden="true">
        {/* Soft Sky Blue Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#f2f9fd] via-[#e6f4fa] to-[#cbecf7]" />

        {/* Vector World Map Background Graphic */}
        <svg
          className="absolute inset-0 h-full w-full opacity-70"
          viewBox="0 0 1440 600"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Detailed World Map Continents Vector */}
          <g fill="#156482" fillOpacity="0.12" stroke="#156482" strokeOpacity="0.18" strokeWidth="1">
            {/* North America */}
            <path d="M 140 120 L 180 105 L 240 100 L 300 115 L 340 130 L 360 155 L 340 180 L 370 200 L 340 235 L 290 245 L 275 275 L 240 280 L 230 250 L 250 220 L 210 200 L 170 230 L 140 190 L 110 150 Z" />
            {/* Central & South America */}
            <path d="M 270 285 L 295 290 L 310 315 L 350 335 L 380 365 L 385 410 L 365 460 L 335 490 L 300 540 L 280 510 L 290 450 L 280 395 L 270 350 L 250 310 Z" />
            {/* Europe */}
            <path d="M 640 110 L 690 100 L 740 105 L 770 135 L 745 165 L 710 175 L 670 185 L 650 160 L 630 140 Z" />
            {/* UK & Ireland */}
            <path d="M 610 125 L 625 120 L 630 135 L 615 145 Z" />
            {/* Africa */}
            <path d="M 650 195 L 725 190 L 785 220 L 815 260 L 795 315 L 765 385 L 725 425 L 685 395 L 665 335 L 640 260 Z" />
            {/* Asia */}
            <path d="M 770 110 L 860 95 L 950 100 L 1060 115 L 1160 130 L 1230 160 L 1190 210 L 1110 240 L 1030 230 L 990 270 L 930 280 L 870 250 L 800 220 L 770 170 Z" />
            {/* India Peninsula */}
            <path d="M 910 245 L 955 250 L 945 310 L 910 330 L 890 270 Z" />
            {/* Japan */}
            <path d="M 1210 170 L 1225 165 L 1235 195 L 1215 210 Z" />
            {/* Australia & Oceania */}
            <path d="M 1120 380 L 1205 370 L 1265 400 L 1255 465 L 1185 495 L 1115 465 L 1100 410 Z" />
          </g>

          {/* Dotted Grid Pattern overlaying continents for travel aesthetic */}
          <pattern id="dot-grid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="#156482" fillOpacity="0.12" />
          </pattern>
          <rect width="1440" height="600" fill="url(#dot-grid)" />

          {/* Soft Location Pin Markers */}
          <g fill="#156482" fillOpacity="0.4">
            {/* North America Pin */}
            <g transform="translate(240, 160) scale(0.75)">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </g>
            {/* Europe Pin */}
            <g transform="translate(710, 140) scale(0.75)">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </g>
            {/* Asia Pin */}
            <g transform="translate(1080, 180) scale(0.75)">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </g>
          </g>

          {/* Transcontinental Flight Routes (Dashed Arc Lines) */}
          {/* Route 1: North America -> Europe */}
          <path
            d="M 245 165 Q 480 70 715 145"
            stroke="#156482"
            strokeWidth="2"
            strokeDasharray="6 6"
            strokeOpacity="0.35"
          />
          {/* Route 2: Europe -> Asia */}
          <path
            d="M 715 145 Q 900 110 1085 185"
            stroke="#156482"
            strokeWidth="2"
            strokeDasharray="6 6"
            strokeOpacity="0.35"
          />

          {/* Airplanes along flight paths */}
          <g transform="translate(480, 105) rotate(10)">
            <path
              d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L14 19v-5.5l8 2.5z"
              fill="#156482"
              opacity="0.45"
            />
          </g>
          <g transform="translate(900, 130) rotate(18)">
            <path
              d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L14 19v-5.5l8 2.5z"
              fill="#156482"
              opacity="0.45"
            />
          </g>
        </svg>
      </div>

      <div className="custom-mx relative z-10 mx-auto max-w-7xl">
        {/* Header */}
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <p className="mb-2 text-xs font-bold tracking-widest text-primary">
            Verified Experiences
          </p>
          <h2
            id="testimonials-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0f2c59]"
          >
            Trusted Stories from Real Travelers
          </h2>
          <p className="mt-3 text-sm sm:text-base text-slate-600 font-medium">
            See how thousands of applicants rely on VisaProofs for fast, embassy-accepted flight itineraries.
          </p>
        </div>

        {/* Slidable Cards Grid matching attached screenshot */}
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-8 pt-2 px-2 -mx-2 min-w-0"
          style={{ scrollBehavior: 'smooth' }}
        >
          {TESTIMONIALS.map((t) => (
            <div
              key={t.id}
              className="shrink-0 w-[300px] sm:w-[360px] md:w-[380px] snap-center"
            >
              {/* White Card matching attached image */}
              <div className="flex h-full w-full flex-col justify-between rounded-3xl border border-white/80 bg-white/95 p-6 sm:p-7 shadow-xl shadow-slate-900/5 backdrop-blur-md transition-all duration-300 hover:shadow-2xl hover:shadow-slate-900/10 hover:-translate-y-1">
                
                <div>
                  {/* Top Row: Avatar + Name/Role */}
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 overflow-hidden rounded-full border-2 border-slate-100 shadow-sm shrink-0 bg-slate-100">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-slate-900 truncate">
                        {t.name}
                      </h3>
                      <p className="text-xs font-medium text-slate-500 truncate mt-0.5">
                        {t.role}
                      </p>
                    </div>
                  </div>

                  {/* Review Quote Text */}
                  <p className="mt-5 text-xs sm:text-sm leading-relaxed text-slate-600 font-normal">
                    "{t.review}"
                  </p>
                </div>

                {/* Rating & Verified Footer */}
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {[...Array(t.rating)].map((_, idx) => (
                      <Star
                        key={idx}
                        className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
                      />
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    {t.verified}
                  </span>
                </div>

              </div>
            </div>
          ))}
        </div>

        {/* Bottom Navigation Buttons (Centered ← → as in screenshot) */}
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            onClick={() => scrollTo('left')}
            aria-label="Previous testimonial"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200/80 bg-white text-slate-700 shadow-xs hover:border-primary hover:bg-tertiary hover:text-primary active:scale-95 transition-all"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.5} />
          </button>
          <button
            onClick={() => scrollTo('right')}
            aria-label="Next testimonial"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200/80 bg-white text-slate-700 shadow-xs hover:border-primary hover:bg-tertiary hover:text-primary active:scale-95 transition-all"
          >
            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>

      </div>
    </section>
  );
}
